class AbortError extends Error {
  public readonly name = 'AbortError';
  public readonly reason?: string;

  constructor(message = 'Request aborted', reason?: string) {
    super(message);
    if (Error.captureStackTrace) Error.captureStackTrace(this, AbortError);
    this.reason = reason;
    // Set the prototype explicitly for proper instanceof checks
    Object.setPrototypeOf(this, AbortError.prototype);
  }

  static isAbortError(error: unknown): error is AbortError {
    return error instanceof AbortError;
  }

  static isAbortRelated(error: unknown): boolean {
    if (AbortError.isAbortError(error)) return true;

    if (error instanceof DOMException && error.name === 'AbortError') return true;

    if (error instanceof Error) {
      if (error.name === 'AbortError') return true;
      const lowerMessage = error.message.toLowerCase();
      if (error.message === 'canceled' || lowerMessage.includes('cancel')) return true;
      if (lowerMessage.includes('abort') || lowerMessage.includes('cancelled')) return true;
    }

    if (error && typeof error === 'object' && '__CANCEL__' in error) return true;

    return false;
  }

  static isSignalAborted(signal?: AbortSignal | null): boolean {
    return signal?.aborted ?? false;
  }

  static fromSignal(signal: AbortSignal, customMessage?: string): AbortError {
    const message = customMessage || 'Request aborted by signal';
    const reason = signal.reason ? String(signal.reason) : undefined;
    return new AbortError(message, reason);
  }

  static fromError(error: unknown, fallbackMessage?: string): Error {
    if (AbortError.isAbortRelated(error)) {
      const originalMessage = error instanceof Error ? error.message : String(error);
      const message = fallbackMessage || originalMessage || 'Operation was aborted';
      const reason = error instanceof Error && 'reason' in error
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ? String((error as any).reason)
        : undefined;
      return new AbortError(message, reason);
    }

    if (error instanceof Error) return error;

    const message = fallbackMessage || 'An unknown error occurred';
    return new Error(message);
  }

  static throwIfAborted(signal?: AbortSignal | null, message?: string): void {
    if (AbortError.isSignalAborted(signal)) throw AbortError.fromSignal(signal!, message);
  }
}

export default AbortError;
